import { useGetProxied } from '~/backend/connector-api/connector-components';
import { APIType } from '~/backend/types';
import AppConfig from '~/utils/appconfig';
import { useLabelingBackend } from './useBackend';

/**
 * A custom hook that retrieves labeling data, deciding between the connector's
 * proxied route and the direct backend based on the deployment mode.
 *
 * In Connector mode (no `backendUrl` configured) fetches labeling through the
 * connector's proxy endpoint (`/proxy/labeling`).
 * Direct backend mode fetches labeling data directly from the backend API.
 *
 * @param {APIType} apiType - The backend API type required (contouring, data, workspace, etc.)
 */
export const useLabelingData = (apiType: APIType) => {
    const isConnectorMode = AppConfig.isConnectorMode;

    // In standalone (non-connector) mode, fetch labeling from the backend.
    // 'enabled' is the opposite of isConnectorMode.
    const { data: backendLabelingData, isLoading: isLoadingBackend } =
        useLabelingBackend(apiType, !isConnectorMode);

    // In connector mode, fetch labeling via the connector's proxied route.
    const { data: proxiedLabelingData, isLoading: isLoadingProxied } =
        useGetProxied(
            {
                pathParams: {
                    rest:
                        apiType === APIType.WORKSPACE
                            ? 'labeling'
                            : `${apiType}/labeling`,
                },
            },
            {
                enabled: isConnectorMode,
            }
        );

    // Return the appropriate data (proxied vs. backend) and its loading state,
    // depending on the deployment mode.
    return {
        data: isConnectorMode ? proxiedLabelingData : backendLabelingData,
        isLoading: isConnectorMode ? isLoadingProxied : isLoadingBackend,
    };
};
